/**
 * Contour+ model proposal card (the runtime HITL gate) for both gated model
 * tools: selecting a model in the Contour+ form, and starting
 * auto-segmentation with it. Pure presentational; nothing happens until the
 * clinician presses Apply. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import ProposalCardShell, {
    ProposalSummaryBox,
    type ProposalStatus,
} from './ProposalCardShell';

export interface ContourModelProposalCardProps {
    readonly action: 'select' | 'start';
    readonly modelLabel: string;
    readonly modelName: string;
    readonly bodyPart: string;
    readonly customizationName?: string;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function ContourModelProposalCard({
    action,
    modelLabel,
    modelName,
    bodyPart,
    customizationName,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: ContourModelProposalCardProps) {
    const isStart = action === 'start';
    return (
        <ProposalCardShell
            title={isStart ? 'Start Contour+' : 'Contour+ model'}
            ariaLabel={
                isStart
                    ? 'Start Contour+ auto-segmentation proposal'
                    : 'Contour+ model selection proposal'
            }
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <ProposalSummaryBox>
                <strong>{modelLabel}</strong> ({modelName})
                {customizationName ? ` · ${customizationName}` : ''}
            </ProposalSummaryBox>
            <Typography variant="caption" color="text.secondary">
                Body part: {bodyPart || 'not specified'}.{' '}
                {isStart
                    ? 'Apply opens the Contour+ form with this model and ' +
                      'starts auto-segmentation.'
                    : 'Apply selects this model in the Contour+ form; ' +
                      'segmentation does not start.'}
            </Typography>
        </ProposalCardShell>
    );
}
